import { useEffect, useState } from 'react';
import { Controller, type SubmitHandler, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { productSchema, type ProductFormInput, type ProductFormOutput } from '../schemas/product.schema';
import { Form, Input, Button, Select, Label, Checkbox, DatePicker } from '@/shared/components';
import { DealerSelect } from '@/features/dealers/components/DealerSelect';
import { useDealerQuery } from '@/features/dealers/hooks/useDealerQuery';
import { useProductStatusesQuery } from '../hooks/useProductStatusesQuery';
import { useProductCapabilitiesQuery } from '../hooks/useProductCapabilitiesQuery';
import { useProductTypesQuery } from '../hooks/useProductTypesQuery';
import type { Dealer } from '@/features/dealers/types/dealers.types';
import type { Product } from '@/features/products/types/products.types';

// `effective_from` travels as a date-only `YYYY-MM-DD` string; the DatePicker works in local Date
// objects. Parse at local midnight (never UTC) so the shown day cannot shift across time zones.
const parseDateOnly = (value?: string | null): Date | null =>
    value ? new Date(`${value.slice(0, 10)}T00:00:00`) : null;

const toDateOnly = (date: Date): string =>
    `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

interface ProductFormProps {
    initialValues?: Partial<Product>;
    selectedDealer?: Dealer | null;
    onSubmit: SubmitHandler<ProductFormOutput>;
    isLoading?: boolean;
    submitLabel?: string;
    onCancel?: () => void;
}

export function ProductForm({ initialValues, selectedDealer: initialSelectedDealer, onSubmit, isLoading = false, submitLabel = 'Save', onCancel }: ProductFormProps) {
    const { handleSubmit, control, formState: { isSubmitting } } = useForm<ProductFormInput, unknown, ProductFormOutput>({
        resolver: zodResolver(productSchema),
        defaultValues: {
            name: initialValues?.name ?? '',
            description: initialValues?.description ?? '',
            status: initialValues?.status_key ?? 'active',
            capabilities: initialValues?.capabilities ?? [],
            effective_from: initialValues?.effective_from ?? '',
            // Prefer the pre-resolved dealer prop (create path) over the raw id from initialValues (edit path).
            parent_dealer_id: initialSelectedDealer?.id ?? initialValues?.parent_dealer_id ?? null,
            // Populate from the flat id, falling back to the nested product_type the show endpoint
            // eager-loads, so an edit opens with the saved type preselected.
            product_type_id: initialValues?.product_type_id ?? initialValues?.product_type?.id ?? '',
        },
    });

    const { data: statusOptions = [] } = useProductStatusesQuery();
    const { data: capabilityOptions = [] } = useProductCapabilitiesQuery();
    const { data: productTypes = [], isPending: productTypesPending } = useProductTypesQuery();

    const baseStatusOptions = statusOptions.map((s) => ({ value: s.key, label: s.label }));
    const currentKey = initialValues?.status_key;
    const statusSelectOptions =
        currentKey && !statusOptions.some((s) => s.key === currentKey)
            ? [...baseStatusOptions, { value: currentKey, label: `${currentKey} (retired)` }]
            : baseStatusOptions;

    // Resolve a pre-existing parent_dealer_id (edit path) into a Dealer object.
    const parentDealerQuery = useDealerQuery(
        initialSelectedDealer ? '' : (initialValues?.parent_dealer_id ?? '')
    );
    const [selectedDealer, setSelectedDealer] = useState<Dealer | null>(initialSelectedDealer ?? null);
    const [syncedInitial, setSyncedInitial] = useState(Boolean(initialSelectedDealer) || !initialValues?.parent_dealer_id);

    useEffect(() => {
        if (syncedInitial) return;
        if (parentDealerQuery.data) {
            setSelectedDealer(parentDealerQuery.data);
            setSyncedInitial(true);
        }
    }, [parentDealerQuery.data, syncedInitial]);

    return (
        <Form className="space-y-4 max-w-2xl" onSubmit={handleSubmit(onSubmit)}>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="md:col-span-2">
                    <Label htmlFor="name">Name</Label>
                    <Controller
                        name="name"
                        control={control}
                        render={({ field, fieldState: { error } }) => (
                            <Input id="name" placeholder="Product name" value={field.value} onChange={field.onChange} onBlur={field.onBlur} error={!!error} hint={error?.message} />
                        )}
                    />
                </div>

                <div className="md:col-span-2">
                    <Label htmlFor="description">Description <span className="text-mp-muted">(optional)</span></Label>
                    <Controller
                        name="description"
                        control={control}
                        render={({ field, fieldState: { error } }) => (
                            <Input id="description" placeholder="Description" value={field.value ?? ''} onChange={field.onChange} onBlur={field.onBlur} error={!!error} hint={error?.message} />
                        )}
                    />
                </div>

                <div>
                    <Label htmlFor="status">Status</Label>
                    <Controller
                        name="status"
                        control={control}
                        render={({ field, fieldState: { error } }) => (
                            <Select
                                id="status"
                                value={field.value}
                                onChange={field.onChange}
                                onBlur={field.onBlur}
                                error={!!error}
                                hint={error?.message}
                                options={statusSelectOptions}
                            />
                        )}
                    />
                </div>

                <div>
                    <Label htmlFor="product_type_id">Product Type</Label>
                    <Controller
                        name="product_type_id"
                        control={control}
                        render={({ field, fieldState: { error } }) => (
                            <Select
                                id="product_type_id"
                                value={field.value ?? ''}
                                isLoading={productTypesPending}
                                onChange={field.onChange}
                                onBlur={field.onBlur}
                                error={!!error}
                                hint={error?.message}
                                placeholder="Select a product type"
                                options={productTypes.map((t) => ({ value: t.id, label: t.name }))}
                            />
                        )}
                    />
                </div>

                <div>
                    <Label>Capabilities</Label>
                    <Controller
                        name="capabilities"
                        control={control}
                        render={({ field }) => (
                            <div className="flex flex-col gap-2 pt-1">
                                {capabilityOptions.map((cap) => (
                                    <Checkbox
                                        key={cap.key}
                                        id={`capability-${cap.key}`}
                                        label={cap.label}
                                        checked={(field.value ?? []).includes(cap.key)}
                                        onChange={(checked) => {
                                            const current = field.value ?? [];
                                            field.onChange(
                                                checked ? [...current, cap.key] : current.filter((k) => k !== cap.key),
                                            );
                                        }}
                                    />
                                ))}
                            </div>
                        )}
                    />
                </div>

                <div>
                    <Label htmlFor="effective_from">Effective From <span className="text-mp-muted">(optional)</span></Label>
                    <Controller
                        name="effective_from"
                        control={control}
                        render={({ field, fieldState: { error } }) => (
                            <DatePicker
                                id="effective_from"
                                value={parseDateOnly(field.value)}
                                onChange={(date) => field.onChange(date ? toDateOnly(date) : '')}
                                placeholder="Select a date"
                                error={!!error}
                                hint={error?.message}
                            />
                        )}
                    />
                </div>

                <div className="md:col-span-2">
                    <Label htmlFor="parent_dealer_id">Parent Dealer <span className="text-mp-muted">(optional)</span></Label>
                    <Controller
                        name="parent_dealer_id"
                        control={control}
                        render={({ field }) => (
                            <DealerSelect
                                selected={selectedDealer}
                                onSelect={(dealer) => {
                                    setSelectedDealer(dealer);
                                    field.onChange(dealer?.id ?? null);
                                }}
                            />
                        )}
                    />
                </div>
            </div>

            <div className="flex items-center gap-2">
                <Button size="sm" disabled={isLoading || isSubmitting}>
                    {isLoading || isSubmitting ? 'Saving...' : submitLabel}
                </Button>
                {onCancel && (
                    <Button size="sm" type="button" variant="outline" onClick={onCancel} disabled={isLoading || isSubmitting}>
                        Cancel
                    </Button>
                )}
            </div>
        </Form>
    );
}
